import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { careers as mockCareers } from '@/data/mock';

const JobApplicationPage = () => {
  const { jobId } = useParams();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    resumeUrl: '',
    coverLetter: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fallbackJob = mockCareers.find((career) => String(career.id) === String(jobId));

    if (!backendUrl) {
      setJob(fallbackJob || null);
      setLoading(false);
      return;
    }

    const fetchJob = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/careers/${jobId}`);
        setJob(response.data);
      } catch (error) {
        console.error('Failed to fetch job:', error);
        setJob(fallbackJob || null);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId, backendUrl]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  if (!job) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h1>
            <Link to="/careers">
              <Button variant="outline">Back to Careers</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast.error('Please accept the privacy policy to continue');
      return;
    }

    setSubmitting(true);

    try {
      const { supabase } = await import('@/lib/supabase');

      const applicationData = {
        job_id: jobId,
        job_title: job.title,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        resume_url: formData.resumeUrl || '',
        cover_letter: formData.coverLetter
      };

      const { error } = await supabase.from('job_applications').insert([applicationData]);

      if (error) throw error;

      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Application error:', error);
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-16">
          <Card className="max-w-2xl w-full">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Application Submitted!</h1>
              <p className="text-lg text-gray-700 mb-8">
                Thank you for applying for the {job.title} position at Redspire Civil Engineering. 
                We have received your application and will review it carefully. If your qualifications 
                match our requirements, we will contact you within 1-2 weeks.
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/careers">
                  <Button variant="outline">View All Positions</Button>
                </Link>
                <Link to="/">
                  <Button className="bg-red-600 hover:bg-red-700">Back to Home</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to={`/careers/${jobId}`}>
            <Button variant="ghost" className="gap-2 mb-6">
              <ArrowLeft className="w-4 h-4" />
              Back to Job Details
            </Button>
          </Link>

          <Card>
            <CardContent className="p-8">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Apply for {job.title}</h1>
                <p className="text-gray-600">{job.location} • {job.type}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+1 (203) 555-0100"
                  />
                </div>

                <div>
                  <Label htmlFor="resume_url">Resume URL (Optional)</Label>
                  <Input
                    id="resume_url"
                    name="resumeUrl"
                    value={formData.resumeUrl}
                    onChange={handleChange}
                    placeholder="Link to your resume (Google Drive, Dropbox, etc.)"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Note: File upload feature coming soon. For now, please provide a shareable link to your resume.
                  </p>
                </div>

                <div>
                  <Label htmlFor="cover_letter">Cover Letter *</Label>
                  <Textarea
                    id="cover_letter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleChange}
                    required
                    rows={8}
                    placeholder="Tell us why you're a great fit for this position..."
                  />
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, consent: Boolean(checked) }))
                    }
                  />
                  <label htmlFor="consent" className="text-sm text-gray-700 leading-relaxed">
                    I agree to the processing of my personal data in accordance with Redspire's{' '}
                    <Link to="/privacy-policy" className="text-red-600 hover:underline">
                      Privacy Policy
                    </Link>
                    . I understand that my application will be reviewed by HR personnel.
                  </label>
                </div>

                <div className="flex gap-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-red-600 hover:bg-red-700 flex-1"
                    size="lg"
                  >
                    {submitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                  <Link to={`/careers/${jobId}`}>
                    <Button type="button" variant="outline" size="lg">
                      Cancel
                    </Button>
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default JobApplicationPage;
